<template>
    <v-container fluid>
        <LoadingBar/>
        <v-form ref="form" lazy-validation>
            <v-row>
                <v-col>
                    <h1>Job Order Update</h1>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-text-field
                        v-model="entityCore.name"
                        label="Name*"
                        placeholder="Name"
                        hint="Minimum of 3 characters required"
                        persistent-hint
                        validate-on="blur"
                        :rules="[
                            rules.required, 
                            rules.min(3), 
                            rules.max(64)
                        ]"
                    >
                    </v-text-field>
                </v-col>
                <v-col>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-text-field
                        v-model="entityCore.work_order_number"
                        label="Job Order Number*"
                        placeholder="Job Order Number"
                        validate-on="blur"
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-text-field>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.work_order_status"
                        :items="workOrderStatuses"
                        item-title="title"
                        item-value="value"
                        label="Status"
                        placeholder="Status"
                        @blur="convertEmptyToNull('work_order_status')"
                    >
                    </v-select>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <v-select
                        v-model="entityCore.contract_uid"
                        :items="contractsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Contract*"
                        placeholder="Contract"
                        @blur="convertEmptyToNull('contract_uid')"
                        @focus="fetchList('contract', 'owner-' + entityAncestry.owner)"
                        :loading="contractsList.loading"
                        :disabled="contractsList.disabled"
                        :hint="contractsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-select>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.stage"
                        :items="workOrderStages"
                        item-title="title"
                        item-value="value"
                        label="Stage"
                        placeholder="Stage"
                        @blur="convertEmptyToNull('stage')"
                    >
                    </v-select>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-text-field
                        v-model="entityCore.location_info"
                        label="Location Info"
                        placeholder="Location info"
                        @blur="convertEmptyToNull('location_info')"
                        :rules="[
                            rules.max(264)
                        ]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-textarea
                        label="Scope of Work*"
                        v-model.lazy="entityCore.scope_of_work"
                        variant="filled"
                        auto-grow
                        :rules="[
                            rules.required,
                            rules.max(3000)
                        ]"
                    ></v-textarea>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Owner Project Managers</h3>
                </v-col>
            </v-row>

        <v-row>
                <v-col>
                    <v-select
                        v-model="entityCore.owner_project_manager_primary_uid"
                        :items="ownerContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Primary Owner Project Manager*"
                        placeholder="Primary Owner Project Manager"
                        @blur="convertEmptyToNull('owner_project_manager_primary_uid')"
                        @focus="fetchList('owner_contact', 'owner-' + entityAncestry.owner)"
                        :loading="isLoading.savedOwnerProjectManagerPrimary || isLoading.ownerContactsList"
                        :hint="ownerContactsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :disabled="alreadySelectedOwnerContacts.includes(item.value)"></v-list-item>
                        </template>
                    </v-select>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.owner_project_manager_2_uid"
                        :items="ownerContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Secondary Owner Project Manager"
                        placeholder="Secondary Owner Project Manager"
                        @blur="convertEmptyToNull('owner_project_manager_2_uid')"
                        @focus="fetchList('owner_contact', 'owner-' + entityAncestry.owner)"
                        :loading="isLoading.savedOwnerProjectManager2 || isLoading.ownerContactsList"
                        :hint="ownerContactsList.hint"
                        :disabled="!entityCore.owner_project_manager_primary_uid"
                        persistent-hint
                        clearable
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :disabled="alreadySelectedOwnerContacts.includes(item.value)"></v-list-item>
                        </template>
                    </v-select>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col cols="6">
                    <v-select
                        v-model="entityCore.owner_project_manager_3_uid"
                        :items="ownerContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Tertiary Owner Project Manager"
                        placeholder="Tertiary Owner Project Manager"
                        @blur="convertEmptyToNull('owner_project_manager_3_uid')"
                        @focus="fetchList('owner_contact', 'owner-' + entityAncestry.owner)"
                        :loading="isLoading.savedOwnerProjectManager3 || isLoading.ownerContactsList"
                        :hint="ownerContactsList.hint"
                        :disabled="!entityCore.owner_project_manager_primary_uid"
                        persistent-hint
                        clearable
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :disabled="alreadySelectedOwnerContacts.includes(item.value)"></v-list-item>
                        </template>
                    </v-select>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>
            
            <v-row>
                <v-col>
                    <h3>Contractor Project Managers</h3>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-select
                        v-model="entityCore.contractor_project_manager_primary_uid"
                        :items="contractorContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Primary Contract Project Manager*"
                        placeholder="Primary Contract Project Manager"
                        @blur="convertEmptyToNull('contractor_project_manager_primary_uid')"
                        @focus="fetchList('contractor_contact', 'contractor-' + contractorDoc._id)"
                        :loading="isLoading.savedContractorProjectManagerPrimary || isLoading.contractorContactsList"
                        :hint="contractorContactsList.hint"
                        :disabled="isLoading.contractContractor"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :disabled="alreadySelectedContractorContacts.includes(item.value)"></v-list-item>
                        </template>
                        <template v-slot:message="{message}">
                            <div class="input-hint" v-html="message"></div>
                        </template>
                    </v-select>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.contractor_project_manager_2_uid"
                        :items="contractorContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Secondary Contractor Project Manager"
                        placeholder="Secondary Contractor Project Manager"
                        @blur="convertEmptyToNull('contractor_project_manager_2_uid')"
                        @focus="fetchList('contractor_contact', 'contractor-' + contractorDoc._id)"
                        :loading="isLoading.savedContractorProjectManager2 || isLoading.contractorContactsList"
                        :hint="contractorContactsList.hint"
                        :disabled="!entityCore.contractor_project_manager_primary_uid"
                        persistent-hint
                        clearable
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :disabled="alreadySelectedContractorContacts.includes(item.value)"></v-list-item>
                        </template>
                        <template v-slot:message="{message}">
                            <div class="input-hint" v-html="message"></div>
                        </template>
                    </v-select>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col cols="6">
                    <v-select
                        v-model="entityCore.contractor_project_manager_3_uid"
                        :items="contractorContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Tertiary Contractor Project Manager"
                        placeholder="Tertiary Contractor Project Manager"
                        @blur="convertEmptyToNull('contractor_project_manager_3_uid')"
                        @focus="fetchList('contractor_contact', 'contractor-' + contractorDoc._id)"
                        :loading="isLoading.savedContractorProjectManager3 || isLoading.contractorContactsList"
                        :hint="contractorContactsList.hint"
                        :disabled="!entityCore.contractor_project_manager_primary_uid"
                        persistent-hint
                        clearable
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :disabled="alreadySelectedContractorContacts.includes(item.value)"></v-list-item>
                        </template>
                        <template v-slot:message="{message}">
                            <div class="input-hint" v-html="message"></div>
                        </template>
                    </v-select>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Approvers</h3>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-select
                        v-model="entityCore.inspector_uid"
                        :items="ownerContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="Inspector*"
                        placeholder="Inspector"
                        @blur="convertEmptyToNull('inspector_uid')"
                        @focus="fetchList('owner_contact', 'owner-' + entityAncestry.owner)"
                        :loading="isLoading.savedInspector || isLoading.ownerContactsList"
                        :hint="ownerContactsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-select>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.rfp_signing_authority_uid"
                        :items="ownerContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="RFP Signing Authority*"
                        placeholder="RFP Signing Authority"
                        @blur="convertEmptyToNull('rfp_signing_authority_uid')"
                        @focus="fetchList('owner_contact', 'owner-' + entityAncestry.owner)"
                        :loading="isLoading.savedRfpSigningAuthority || isLoading.ownerContactsList"
                        :hint="ownerContactsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-select>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <v-select
                        v-model="entityCore.ntp_signing_authority_uid"
                        :items="ownerContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="NTP Signing Authority*"
                        placeholder="NTP Signing Authority"
                        @blur="convertEmptyToNull('ntp_signing_authority_uid')"
                        @focus="fetchList('owner_contact', 'owner-' + entityAncestry.owner)"
                        :loading="isLoading.savedNtpSigningAuthority || isLoading.ownerContactsList"
                        :hint="ownerContactsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-select>
                </v-col>
                <v-col>
                    <v-select
                        v-model="entityCore.woa_signing_authority_uid"
                        :items="clientContactsList.items"
                        item-title="_name"
                        item-value="_id"
                        label="JOA Signing Authority*"
                        placeholder="JOA Signing Authority"
                        @blur="convertEmptyToNull('woa_signing_authority_uid')"
                        @focus="fetchList('client_contact', 'client-' + clientDoc._id)"
                        :loading="clientContactsList.loading"
                        :disabled="clientContactsList.disabled"
                        :hint="clientContactsList.hint"
                        persistent-hint
                        :rules="[
                            rules.required,
                        ]"
                    >
                    </v-select>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Calculated Dates</h3>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="6">
                    <v-text-field
                        v-model="entityCore.duration"
                        label="Duration*"
                        placeholder="Duration"
                        validate-on="blur"
                        @input="numbersOnly($event, 'duration');"
                        @blur="convertToNumber('duration')"
                        persistent-hint
                        hint="Calendar days"
                        :rules="[
                            rules.required,
                            rules.numbersOnly,
                        ]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
            

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Administrative Dates</h3>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.site_walk_through_date"
                        label="Site Walk Through Date"
                        @blur="convertToDateObject($event, 'site_walk_through_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.rfp_issue_date"
                        label="RFP Issue Date"
                        @blur="convertToDateObject($event, 'rfp_issue_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.proposal_due_date"
                        label="Proposal Due Date*"
                        @blur="convertToDateObject($event, 'proposal_due_date')"
                        validate-on="blur"
                        :rules="[
                            rules.required,
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.accepted_proposal_date"
                        label="Accepted Proposal Date"
                        @blur="convertToDateObject($event, 'accepted_proposal_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.work_order_authorization_date"
                        label="Job Order Authorization Date"
                        @blur="convertToDateObject($event, 'work_order_authorization_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.ntp_issued_date"
                        label="NTP Issued Date"
                        @blur="convertToDateObject($event, 'ntp_issued_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.eeo_clearance_date"
                        label="EEO Clearance Date"
                        @blur="convertToDateObject($event, 'eeo_clearance_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
                <v-col>
                    <v-date-input
                        prepend-icon=""
                        prepend-inner-icon="$calendar"
                        v-model.lazy="entityCore.notice_of_completion_file_date"
                        label="Notice of Completion File Date"
                        @blur="convertToDateObject($event, 'notice_of_completion_file_date')"
                        validate-on="blur"
                        :rules="[
                            rules.date,
                        ]"
                    ></v-date-input>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row>
                <v-col>
                    <h3>Fees & Damages</h3>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <v-text-field
                        v-model="entityCore.liquidated_damages_to_district"
                        label="Liquidated Damages to District*"
                        placeholder="Liquidated Damages to District"
                        @blur="convertToNumber('liquidated_damages_to_district')"
                        @input="numbersAndDecimalsOnly($event, 'liquidated_damages_to_district');"
                        validate-on="blur"
                        hint="Per calendar day"
                        persistent-hint
                        prepend-inner-icon="mdi-currency-usd"
                        :rules="[
                            rules.required,
                            rules.numberWithDecimal
                        ]"
                    >
                    </v-text-field>
                </v-col>
                <v-col>
                    <v-text-field
                        v-model="entityCore.liquidated_damages_to_contractor"
                        label="Liquidated Damages to Contractor*"
                        placeholder="Liquidated Damages to Contractor"
                        @blur="convertToNumber('liquidated_damages_to_contractor')"
                        @input="numbersAndDecimalsOnly($event, 'liquidated_damages_to_contractor');"
                        validate-on="blur"
                        hint="Per calendar day"
                        persistent-hint
                        prepend-inner-icon="mdi-currency-usd"
                        :rules="[
                            rules.required,
                            rules.numberWithDecimal
                        ]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col cols="6">
                    <v-text-field
                        v-model="entityCore.jamb_fee_percentage"
                        label="JAMB Fee Percentage*"
                        placeholder="JAMB Fee Percentage"
                        @blur="convertToNumber('jamb_fee_percentage')"
                        @input="numbersAndDecimalsOnly($event, 'jamb_fee_percentage');"
                        validate-on="blur"
                        append-inner-icon="mdi-percent"
                        :rules="[
                            rules.required,
                            rules.numberWithDecimal
                        ]"
                    >
                    </v-text-field>
                </v-col>
            </v-row>
            
            

            <FormUpdateFooter 
                :form="form"
                :entityCore="entityCore"
                :current="current"
            />
        </v-form>
    </v-container>
</template>
<script setup>
import { useStore } from 'vuex';
import { ref, reactive, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import LoadingBar from "@/components/interface/LoadingBar.vue";
import FormUpdateFooter from "@/components/interface/FormUpdateFooter.vue";
import rules from "@/assets/modules/rules.js";
import workOrderStatuses from "@/assets/selects/project-work-order-status.json";
import workOrderStages from "@/assets/selects/project-work-order-stage.json";
import { useIsCurrentEntityLoaded, useEntityCore, useToDateObject, useFetchSingle, useFetchList, useEntityAncestry, useFieldValidation, useNameById } from "@/assets/modules/composables.js";
// import useCalculatedDateData from "@/assets/modules/workOrderCalculatedDateData.js";

const store = useStore();
const route = useRoute();
const form = ref();
const isLoading = reactive({});
const alreadyFetchedData = [];
const { isCurrentEntityLoaded } = useIsCurrentEntityLoaded();
const { entityCore } = useEntityCore();
const { toDateObject} = useToDateObject(entityCore);
// const { calculatedDateData } = useCalculatedDateData(entityCore);
const { fetchSingle } = useFetchSingle(isLoading, alreadyFetchedData);
const { fetchList } = useFetchList(isLoading, alreadyFetchedData);
const { entityAncestry } = useEntityAncestry();
const { numbersOnly, convertEmptyToNull, convertToNumber, numbersAndDecimalsOnly, convertToDateObject } = useFieldValidation(entityCore);   
const { nameById } = useNameById();
const current = {
    id: route.params.entity_id,
    type: "supplemental_work_order"
};


// Convert date to date objects to avoid crashing the date input component
watch(isCurrentEntityLoaded, async () => {
    if(isCurrentEntityLoaded.value){
        toDateObject("construction_start_date");
        toDateObject("site_walk_through_date");
        toDateObject("rfp_issue_date");
        toDateObject("proposal_due_date");
        toDateObject("accepted_proposal_date");
        toDateObject("work_order_authorization_date");
        toDateObject("ntp_issued_date");
        toDateObject("eeo_clearance_date");
        toDateObject("notice_of_completion_file_date");
    }
})





watch(isCurrentEntityLoaded, async () => {
    if(isCurrentEntityLoaded.value){
        fetchSingle("owner_contact", entityCore.value.owner_project_manager_primary_uid);
        fetchSingle("owner_contact", entityCore.value.owner_project_manager_2_uid);
        fetchSingle("owner_contact", entityCore.value.owner_project_manager_3_uid);
        fetchSingle("contractor_contact", entityCore.value.contractor_project_manager_primary_uid);
        fetchSingle("contractor_contact", entityCore.value.contractor_project_manager_2_uid)
        fetchSingle("contractor_contact", entityCore.value.contractor_project_manager_3_uid);
        fetchSingle("owner_contact", entityCore.value.inspector_uid);
        fetchSingle("owner_contact", entityCore.value.rfp_signing_authority_uid);
        fetchSingle("owner_contact", entityCore.value.ntp_signing_authority_uid);
        fetchSingle("client_contact", entityCore.value.woa_signing_authority_uid);
        await fetchSingle("contract", entityCore.value.contract_uid);
        fetchSingle("contractor",contractDoc.value.core.contractor_uid);
    }
})

// Make an array to reference which owner contacts IDs have already been selected
const alreadySelectedOwnerContacts = computed(() => {
    return [
        entityCore.value.owner_project_manager_primary_uid, 
        entityCore.value.owner_project_manager_2_uid, 
        entityCore.value.owner_project_manager_3_uid
    ];
})
const alreadySelectedContractorContacts = computed(() => {
    return [
    entityCore.value.contractor_project_manager_primary_uid,
    entityCore.value.contractor_project_manager_2_uid,
    entityCore.value.contractor_project_manager_3_uid
    ];
})



const ownerDoc = computed(() => {
    // console.log("entityAncestry.value?.owner", entityAncestry.value?.owner)
    return store.getters.getEntityDocument(entityAncestry.value?.owner);
})
// Need this to get the client document
const projectDoc = computed(() => {
    return store.getters.getEntityDocument(entityAncestry.value?.project);
})
// Need this for populating the client contacts list
const clientDoc = computed(() => {
    return store.getters.getEntityDocument(projectDoc.value?.core?.client_uid);
})
const contractorDoc = computed(() => {
    return store.getters.getEntityDocument(contractDoc.value?.core?.contractor_uid);
})
const contractDoc = computed(() => {
    return store.getters.getEntityDocument(entityCore.value.contract_uid);
})

// Get the client document when the project document is loaded
// This is needed for the JOA Signing Authority field
watch(
    () => projectDoc,
    async (newVal, oldVal) => {
        if(oldVal !== undefined && !alreadyFetchedData.includes(projectDoc.value?.core?.client_uid)){
            fetchSingle("client", projectDoc.value?.core?.client_uid);
        }
    },
    { deep: true, immediate: true }
)


const contractsList = computed(() => {
    const items = store.getters.getChildList("owner." + entityAncestry.value.owner + ".", "contract");
    const hint = nameById(entityAncestry.value.owner, null) 
        ? `List of contracts for ${nameById(entityAncestry.value.owner, null)}` 
        : 'List of contracts'
    const disabled = !entityAncestry.value.owner;
    const loading = isLoading['contract_owner-' + entityAncestry.value.owner];
    return {
        items: items,
        hint: hint,
        disabled: disabled,
        loading: loading,
    };
})
const clientContactsList = computed(() => {
    const items = store.getters.getChildList("client." + clientDoc.value?._id + ".", "client_contact");
    const hint = clientDoc.value?._name 
        ? `List of client contacts for ${clientDoc.value?._name} via ${projectDoc.value?._name}` 
        : 'List of client contacts'
    const disabled = !clientDoc.value?._name;
    const loading = isLoading['client_contact_client-' + clientDoc.value?._id];
    return {
        items: items,
        hint: hint,
        disabled: disabled,
        loading: loading,
    };
})
const ownerContactsList = computed(() => {
    let hint = 'List of owner contacts';
    if(ownerDoc.value){
        hint = `List of owner contacts from ${ownerDoc.value?._name}`;
    }
    const listObject = {
        items: store.getters.getChildList("owner."+entityAncestry.value.owner+".", "owner_contact"),
        hint: hint,
    };
    return listObject;
})
const contractorContactsList = computed(() => {
    let hint = 'List of contractor contacts<br>Choose a Contract to enable.';
    if(contractorDoc.value){
        hint = `List of contractor contacts from ${contractorDoc.value?._name}`;
    }
    const listObject = {
        items: store.getters.getChildList("contractor."+contractDoc.value?.core?.contractor_uid+".", "contractor_contact"),
        hint: hint,
    };
    return listObject;
})


watch(
    () => entityCore.value.contract_uid,
    async (newVal, oldVal) => {
        if(newVal !== oldVal && oldVal !== undefined){
            const contractId = newVal;
            entityCore.value.contractor_project_manager_primary_uid = null;
            entityCore.value.contractor_project_manager_2_uid = null;
            entityCore.value.contractor_project_manager_3_uid = null;

            fetchSingle("contract", contractId);
            const contractorId = store.getters.getEntityDocument(contractId)?.core?.contractor_uid;
            fetchSingle("contractor", contractorId);
        }
    },
    { deep: true }
)
</script>